import { i18n } from '@lingui/core'
import { t, Trans } from '@lingui/macro'
import { Checkbox, Divider, FormControlLabel, Typography } from '@mui/material'
import { ReduxState } from 'app/redux/ReduxInterfaces'
import { ErrorMessage, useFormikContext } from 'formik'
import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import Radio from '../common-components/CustomRadio'
import { NumberFormatCustom } from '../common-components/FormElement'
import { ErrorLabel } from '../common-components/forms/ErrorLabel'
import { FormTopLabel } from '../common-components/forms/FormTopLabel'
import { TextFieldWithFormik } from '../common-components/with-formik/TextFieldWithFormik'
import { getLabelFromTranslationData } from '../common/translationsCommon'
import { indexToOption } from './QuestionWidget'
import { useSurveyContext } from './SurveyContext'
import './SurveyGroupQuestion.scss'
import { nonApplicableSuffix } from './SurveyTab'
import { GroupQuestion, SurveyQuestionOption } from './surveyTypes'

export const SurveyGroupQuestion: React.FC<{ groupQuestions: GroupQuestion[], disabled: boolean, required: boolean, id: string, options: SurveyQuestionOption[], hideError: boolean }> = ({ groupQuestions, options, disabled, id, required, hideError }) => {
  const langVersion = useSelector<ReduxState, string>(state => state.user.language)

  const { setFieldValue, setValues, values } = useFormikContext<Record<string, any>>()
  const { disabledFields } = useSurveyContext()
  const questionValue = values[id]
  const nonApplicableValue = values[id + nonApplicableSuffix]

  const handleChange = useCallback((questionIndex: number, optionIndex: number) => (e: React.SyntheticEvent, checked: boolean): void => {
    void setFieldValue(`${id}.${questionIndex}`, indexToOption(optionIndex))
    if (values[id + '_other'] != null && values[id + '_other'][questionIndex] != null) {
      void setFieldValue(`${id}_other.${questionIndex}`, null)
    }
  }, [setFieldValue, id, values])

  return (
    <div className='survey-question-container'>
      {groupQuestions.map((question, index) => {
        const { titleValue } = question

        if (disabledFields.includes(question.id)) {
          return null
        }

        const nonApplicableSelected: boolean = nonApplicableValue?.[index]
        const showError = !nonApplicableSelected && !questionValue[index]

        return (
          <div className='survey-question-card' key={index} id={question.id}>
            <FormTopLabel required={required}>
              {getLabelFromTranslationData({
                langVersion,
                data: titleValue
              })}
            </FormTopLabel>
            <div>
              {options.map((option, optionIndex) => {
                const { text, requireDetails = 'number' } = option

                const isChecked = questionValue[index] === indexToOption(optionIndex)

                return (
                  <div key={optionIndex}>
                    <FormControlLabel
                      value={indexToOption(optionIndex)}
                      control={
                        <Radio
                          disabled={disabled || nonApplicableSelected}
                          checked={isChecked}
                          sx={{
                            color: !hideError && showError && required
                              ? 'error.main'
                              : undefined
                          }}
                        />
}
                      label={
                        <Typography className='survey-question-text' style={{ textAlign: 'left' }}>
                          {text}
                        </Typography>
                            }
                      onChange={handleChange(index, optionIndex)}
                      disabled={disabled || nonApplicableSelected}
                    />
                    {isChecked && requireDetails != null && (
                      <TextFieldWithFormik
                        name={`${id}_other.${index}`}
                        style={{ marginLeft: 15, marginRight: 15 }}
                        variant='outlined'
                        placeholder={i18n._(t`Please, specify`)}
                        fullWidth
                        disabled={disabled || nonApplicableSelected}
                        InputProps={
                                requireDetails === 'number'
                                  ? { inputComponent: NumberFormatCustom }
                                  : {}
                              }
                      />
                    )}
                  </div>
                )
              })}
            </div>

            {question.nonApplicable &&
              <FormControlLabel
                control={<Checkbox checked={nonApplicableSelected} />}
                label={
                  <Typography className='survey-question-text' style={{ textAlign: 'left' }}>
                    <Trans>Is non applicable</Trans>
                  </Typography>
}
                onChange={e => {
                  const toSet = { ...values }
                  toSet[id][index] = ''
                  if (toSet[id + nonApplicableSuffix] == null) {
                    toSet[id + nonApplicableSuffix] = []
                  }
                  toSet[id + nonApplicableSuffix][index] = (e.target as HTMLInputElement).checked
                  void setValues(toSet)
                }}
                disabled={disabled}
              />}

            {Boolean(
              !hideError && showError
            ) && (
              <ErrorMessage name={`${id}.${index}`}>
                {msg => (
                  <ErrorLabel label={msg} />
                )}
              </ErrorMessage>
            )}

            {index !== groupQuestions.length - 1 &&
              <div style={{ width: '100%' }}>
                <Divider />
              </div>}

          </div>
        )
      })}
    </div>
  )
}
